import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  getDatabricksUsers,
  getDatabricksTopUsersByQueries,
  getDatabricksTopUsersByDataRead,
  getDatabricksUserDetail,
  getDatabricksUserTrends,
  getDatabricksUserStatementBreakdown,
  getDatabricksUserFilters,
  getDatabricksServicePrincipalConfig,
  upsertDatabricksServicePrincipalConfig,
  type EntityFilterParams,
} from "@/helpers/apis";
import {
  DatabricksUsersParams,
  DatabricksUserDetailParams,
  DatabricksUserTrendsParams,
  ServicePrincipalConfig,
} from "../types";

export const DATABRICKS_SP_CONFIG_KEY = "databricks-service-principal-config";

export const useDatabricksUsers = (params: DatabricksUsersParams = {}, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-users",
      params.start_date,
      params.end_date,
      params.workspace_id,
      params.limit,
      params.user_names,
      params.user_types,
      params.page,
      params.page_size,
      params.sortAttribute,
      params.sortOrder,
    ],
    queryFn: () => getDatabricksUsers(params),
    enabled,
    placeholderData: keepPreviousData,
  });
};

export const useDatabricksTopUsersByQueries = (
  params: DatabricksUsersParams = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-top-users-by-queries",
      params.start_date,
      params.end_date,
      params.workspace_id,
      params.limit,
    ],
    queryFn: () => getDatabricksTopUsersByQueries(params),
    enabled,
  });
};

export const useDatabricksTopUsersByDataRead = (
  params: DatabricksUsersParams = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-top-users-by-data-read",
      params.start_date,
      params.end_date,
      params.workspace_id,
      params.limit,
    ],
    queryFn: () => getDatabricksTopUsersByDataRead(params),
    enabled,
  });
};

export const useDatabricksUserDetail = (params: DatabricksUserDetailParams, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-user-detail", params.user_name, params.start_date, params.end_date],
    queryFn: () => getDatabricksUserDetail(params),
    enabled: enabled && Boolean(params.user_name),
  });
};

export const useDatabricksUserTrends = (params: DatabricksUserTrendsParams, enabled = true) => {
  return useQuery({
    queryKey: [
      "databricks-user-trends",
      params.user_name,
      params.start_date,
      params.end_date,
      params.granularity,
    ],
    queryFn: () => getDatabricksUserTrends(params),
    enabled: enabled && Boolean(params.user_name && params.start_date && params.end_date),
  });
};

export const useDatabricksUserStatementBreakdown = (
  params: DatabricksUserDetailParams,
  enabled = true
) => {
  return useQuery({
    queryKey: [
      "databricks-user-statement-breakdown",
      params.user_name,
      params.start_date,
      params.end_date,
    ],
    queryFn: () => getDatabricksUserStatementBreakdown(params),
    enabled: enabled && Boolean(params.user_name),
  });
};

export const useDatabricksUserFilters = (params: EntityFilterParams = {}, enabled = true) => {
  return useQuery({
    queryKey: ["databricks-user-filters", params.workspace_id, params.prefix],
    queryFn: () => getDatabricksUserFilters(params),
    enabled,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};

export const useDatabricksServicePrincipalConfig = (enabled = true) =>
  useQuery({
    queryKey: [DATABRICKS_SP_CONFIG_KEY],
    queryFn: getDatabricksServicePrincipalConfig,
    enabled,
    staleTime: 5 * 60 * 1000,
  });

export const useUpsertDatabricksServicePrincipalConfig = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ServicePrincipalConfig) => upsertDatabricksServicePrincipalConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DATABRICKS_SP_CONFIG_KEY] });
      queryClient.invalidateQueries({ queryKey: ["databricks-users"] });
      queryClient.invalidateQueries({ queryKey: ["databricks-user-filters"] });
    },
  });
};
